import { shareInFlight } from './apiCache.js';
import { seasonQuery as buildSeasonQuery } from './season.js';

// API Configuration - requires VITE_API_URL environment variable
const API_BASE_URL = import.meta.env?.VITE_API_URL;

/**
 * Auth API for Yahoo OAuth.
 *
 * All methods accept an optional `token` string so they can be called from
 * outside React components (e.g. OAuthCallback page). When a token is
 * provided the request includes an Authorization: Bearer header.
 */
export const authApi = {
  /**
   * Get Yahoo OAuth URL for authorization.
   * @param {string} [token] - Clerk JWT
   */
  async getOAuthUrl(token) {
    const response = await fetch(`${API_BASE_URL}/auth/yahoo/url`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const json = await response.json();
    if (!json.success) throw new Error(json.error || 'Failed to get OAuth URL');
    return json.data;
  },

  /**
   * Exchange authorization code for tokens.
   * @param {string} code
   * @param {string} state
   * @param {string} [token] - Clerk JWT
   */
  async handleCallback(code, state, token) {
    const response = await fetch(`${API_BASE_URL}/auth/yahoo/callback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ code, state }),
    });
    return response.json();
  },

  /**
   * Fetch user's leagues.
   * @param {string} [token] - Clerk JWT
   */
  async getLeagues(token) {
    const response = await fetch(`${API_BASE_URL}/auth/yahoo/leagues`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const json = await response.json();
    if (!json.success) throw new Error(json.error || 'Failed to fetch leagues');
    return json.data;
  },

  /**
   * Disconnect Yahoo OAuth credentials for the current user.
   * @param {string} [token] - Clerk JWT
   */
  async disconnectYahoo(token) {
    const response = await fetch(`${API_BASE_URL}/auth/yahoo/disconnect`, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.json();
  },
};

/**
 * API for ESPN's cookie-based auth (no OAuth — user pastes espn_s2/SWID).
 */
export const espnApi = {
  /**
   * Store ESPN private-league cookies for the current user.
   * @param {string} espnS2
   * @param {string} swid
   * @param {string} [token] - Clerk JWT
   */
  async connect(espnS2, swid, token) {
    const response = await fetch(`${API_BASE_URL}/auth/espn/connect`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ espn_s2: espnS2, swid }),
    });
    return response.json();
  },

  /**
   * Disconnect ESPN cookies for the current user.
   * @param {string} [token] - Clerk JWT
   */
  async disconnect(token) {
    const response = await fetch(`${API_BASE_URL}/auth/espn/disconnect`, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.json();
  },

  /**
   * Fetch ESPN leagues this user has already added (no ESPN discovery endpoint exists).
   * @param {string} [token] - Clerk JWT
   */
  async getLeagues(token) {
    const response = await fetch(`${API_BASE_URL}/auth/espn/leagues`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const json = await response.json();
    if (!json.success) throw new Error(json.error || 'Failed to fetch ESPN leagues');
    return json.data;
  },

  /**
   * Validate cookies against a real league, register it, and join it.
   * @param {string} leagueId - raw numeric ESPN league ID
   * @param {number} season
   * @param {string} [token] - Clerk JWT
   */
  async addLeague(leagueId, season, token) {
    const response = await fetch(`${API_BASE_URL}/espn/leagues`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ league_id: leagueId, season }),
    });
    const json = await response.json();
    if (!json.success) throw new Error(json.error || 'Failed to add ESPN league');
    return json.data;
  },
};

/** The Ledger Home summary: one entry per league the user has added. */
export const homeApi = {
  async get(token) {
    const response = await fetch(`${API_BASE_URL}/me/home`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const json = await response.json();
    if (!json.success) throw new Error(json.error || 'Failed to load your leagues');
    return json.data;
  },
};

/**
 * Create API client for a specific league.
 *
 * @param {string} leagueId - The league ID
 * @param {Function} [authFetch=fetch] - Fetch function that injects auth
 *   headers. Pass `authFetch` from `useClerkApi()` in authenticated views.
 *   Defaults to plain `fetch` for backward-compat with example/public pages.
 * @param {number} [season=null] - Optional season year to scope requests to
 * @param {string} [apiBaseUrl=API_BASE_URL] - API base URL. Exposed for deterministic boundary tests.
 * @returns {Object} API methods for the league
 */
export function createLeagueApi(leagueId, authFetch = fetch, season = null, apiBaseUrl = API_BASE_URL) {
  const baseUrl = `${apiBaseUrl}/leagues/${leagueId}`;
  const seasonQuery = buildSeasonQuery(season);
  if (seasonQuery == null) throw new Error('Invalid season');

  function fetchApi(endpoint, options = {}) {
    if (options.method && options.method !== 'GET') return fetchApiOnce(endpoint, options);
    return shareInFlight(`${baseUrl}${endpoint}`, () => fetchApiOnce(endpoint, options));
  }

  async function fetchApiOnce(endpoint, options = {}) {
    if (!apiBaseUrl) {
      throw new Error(
        'VITE_API_URL is not set. Create frontend/.env.development with VITE_API_URL=<your api endpoint>'
      );
    }
    const url = `${baseUrl}${endpoint}`;
    try {
      const response = await authFetch(url, options);
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) throw new Error('Unauthorized');
        if (response.status === 404) throw new Error('League not found');
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const json = await response.json();
      if (json.success) return json.data;
      throw new Error(json.error || 'API request failed');
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
      throw error;
    }
  }

  function scopedQuery(throughWeek) {
    if (!Number.isInteger(throughWeek) || throughWeek <= 0 || throughWeek > 99) {
      throw new Error('Invalid through week');
    }
    return `${seasonQuery}${seasonQuery ? '&' : '?'}through_week=${throughWeek}`;
  }

  // Recap endpoints answer 404 with a coded body (`not_ready: …`) that the
  // page treats as a state, not an error, so the code must reach the caller.
  function fetchCoded(endpoint, options = {}) {
    if (options.method && options.method !== 'GET') return fetchCodedOnce(endpoint, options);
    return shareInFlight(`${baseUrl}${endpoint}`, () => fetchCodedOnce(endpoint, options));
  }

  async function fetchCodedOnce(endpoint, options = {}) {
    const response = await authFetch(`${baseUrl}${endpoint}`, options);
    if (!response.ok && response.status !== 401 && response.status !== 403) {
      const json = await response.json().catch(() => null);
      if (json?.error) throw new Error(json.error);
    }
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) throw new Error('Unauthorized');
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const json = await response.json();
    if (json.success) return json.data;
    throw new Error(json.error || 'API request failed');
  }

  function withWeek(week) {
    if (!Number.isInteger(week) || week <= 0 || week > 99) throw new Error('Invalid week');
    return `${seasonQuery}${seasonQuery ? '&' : '?'}week=${week}`;
  }

  async function getRecapContext(throughWeek) {
    const query = scopedQuery(throughWeek);
    const [standings, matchups] = await Promise.all([
      fetchApi(`/standings${query}`),
      fetchApi(`/matchups${query}`),
    ]);
    return { standings, matchups };
  }

  return {
    baseUrl: apiBaseUrl,
    getStandings: () => fetchApi(`/standings${seasonQuery}`),
    getTeams: () => fetchApi(`/teams${seasonQuery}`),
    getPowerRankings: () => fetchApi(`/power-rankings${seasonQuery}`),
    getLuckIndex: () => fetchApi(`/luck-index${seasonQuery}`),
    getVsMedian: () => fetchApi(`/vs-median${seasonQuery}`),
    getWeeklyScores: (week) => fetchApi(`${week ? `/weekly-scores/${week}` : '/weekly-scores'}${seasonQuery}`),
    getMatchups: (week) => fetchApi(`${week ? `/matchups/${week}` : '/matchups'}${seasonQuery}`),
    getRecapContext,
    // Stored weekly recap (backend sweep 3)
    getRecap: async (week) => fetchCoded(`/recap${withWeek(week)}`),
    getRecapUnread: () => fetchCoded(`/recap/unread${seasonQuery}`),
    markRecapRead: (week) => fetchCoded(`/recap/read${seasonQuery}`, { method: 'POST', body: JSON.stringify({ week }) }),
    getRecapCatchUp: () => fetchCoded(`/recap/catch-up${seasonQuery}`),
    getRecapScenarios: (picks) => fetchCoded(`/recap/scenarios${seasonQuery}`, { method: 'POST', body: JSON.stringify({ picks }) }),
    getLineupGrades: () => fetchCoded(`/lineup-grades${seasonQuery}`),
    getProjectionDelta: () => fetchCoded(`/projection-delta${seasonQuery}`),
    getDraftGrades: () => fetchCoded(`/draft-grades${seasonQuery}`),
    getTradeLedger: () => fetchCoded(`/trade-ledger${seasonQuery}`),
    getPlayoffOdds: () => fetchCoded(`/playoff-odds${seasonQuery}`),
    getSacko: () => fetchCoded(`/sacko${seasonQuery}`),
    putSacko: (text) => fetchCoded(`/sacko${seasonQuery}`, { method: 'PUT', body: JSON.stringify({ text }) }),
    getTrivia: () => fetchCoded('/trivia'),
    castVote: (week, category, teamId) => fetchCoded('/votes', { method: 'POST', body: JSON.stringify({ week, category, team_id: teamId }) }),
    getVoteTrophies: () => fetchCoded('/vote-trophies'),
    answerTrivia: (week, question, choice) => fetchCoded('/trivia/answers', { method: 'POST', body: JSON.stringify({ week, question, choice }) }),
    getHeatmap: () => fetchApi(`/heatmap${seasonQuery}`),
    getActivity: () => fetchApi(`/activity${seasonQuery}`),
    getWeeklyExtremes: () => fetchApi(`/weekly-extremes${seasonQuery}`),
    getPlayoffSettings: () => fetchApi(`/playoff-settings${seasonQuery}`),
    getDashboard: () => fetchApi(`/dashboard${seasonQuery}`),
    getRoster: (teamId) => fetchApi(`/roster/${teamId}${seasonQuery}`),
    getWeekPlayers: (week) => fetchApi(`/week-players/${week}${seasonQuery}`),

    // Season browsing (sub-project 4) — not season-scoped themselves, so no seasonQuery
    getSeasons: () => fetchApi('/seasons'),
    getHistory: (through = null) => fetchApi(through == null ? '/history' : `/history?through=${encodeURIComponent(through)}`),
    getRivalry: (a, b, through = null) => fetchCoded(`/rivalry?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}${through == null ? '' : `&through=${encodeURIComponent(through)}`}`),
    getWhatIf: (season, changes) => fetchCoded(`/what-if?season=${encodeURIComponent(season)}${changes ? `&changes=${encodeURIComponent(changes)}` : ''}`),
    getViewerAliases: () => fetchApi('/viewer-aliases'),
    triggerBackfill: (backfillSeason) => fetchApi(`/seasons/${backfillSeason}/backfill`, { method: 'POST' }),
    discoverSeason: (discoverSeason) => fetchApi('/seasons/discover', {
      method: 'POST',
      body: JSON.stringify({ season: discoverSeason }),
    }),

    // Personal team endpoints (require auth) — franchise-level, not season-scoped
    joinLeague: () =>
      fetchApi('/join', { method: 'POST' }),
    getSyncJob: (jobId) => fetchApi(`/sync-jobs/${encodeURIComponent(jobId)}`),
    setMyTeam: (teamId) =>
      fetchApi(`/my-team${seasonQuery}`, {
        method: 'PUT',
        body: JSON.stringify({ team_id: teamId }),
      }),
    getShare: () => fetchApi('/share'),
    enableShare: () => fetchApi('/share', { method: 'POST' }),
    resetShare: () => fetchApi('/share/reset', { method: 'POST' }),
    disableShare: () => fetchApi('/share', { method: 'DELETE' }),
  };
}

/** The base a share link's reads go through: /shared/{token}/leagues/{id}/... */
export const sharedApiBase = (token) => `${API_BASE_URL}/shared/${encodeURIComponent(token)}`;

export const sharedApi = {
  /** The league a share token opens; rejects with code 'link_inactive' on 404. */
  async getLeague(token) {
    const response = await fetch(sharedApiBase(token));
    const json = await response.json().catch(() => ({}));
    if (response.status === 404) throw Object.assign(new Error('link_inactive'), { code: 'link_inactive' });
    if (!response.ok || !json.success) throw new Error(json.error || `HTTP error! status: ${response.status}`);
    return json.data;
  },
};

/**
 * Admin API for sync and maintenance operations.
 */
export const adminApi = {
  /**
   * Trigger a sync for a league the current Clerk user belongs to.
   * @param {string} leagueId
   * @param {string} token - Clerk JWT
   */
  async triggerSync(leagueId, token) {
    const response = await fetch(`${API_BASE_URL}/leagues/${leagueId}/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({}),
    });
    const json = await response.json();
    if (!json.success) throw new Error(json.error || 'Failed to trigger sync');
    return json.data;
  },

  /**
   * Check if a league exists in the database (standings only).
   * @deprecated Prefer checkLeagueReady for more complete status.
   */
  async checkLeagueExists(leagueId) {
    try {
      const response = await fetch(`${API_BASE_URL}/leagues/${leagueId}/standings`);
      if (response.status === 404) return false;
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return true;
    } catch (error) {
      if (error.message.includes('404') || error.message.includes('not found')) return false;
      throw error;
    }
  },

  /**
   * The league's readiness for `season` (null = live), and whether that season
   * is one the league has. Ready only when league data and computed analytics
   * are. A failed request reads as not ready, season known.
   */
  async checkLeagueReadiness(leagueId, season = null) {
    try {
      const query = season != null ? `?season=${encodeURIComponent(season)}` : '';
      const response = await fetch(`${API_BASE_URL}/leagues/${leagueId}/ready${query}`);
      if (!response.ok) return { ready: false, seasonKnown: true };
      const json = await response.json();
      return { ready: !!json?.data?.ready, seasonKnown: json?.data?.season_known !== false };
    } catch {
      return { ready: false, seasonKnown: true };
    }
  },

  async checkLeagueReady(leagueId, season = null) {
    return (await adminApi.checkLeagueReadiness(leagueId, season)).ready;
  },
};

// Default export for backward compatibility
export const api = createLeagueApi('40774');
export default api;
